import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {HTMLElement | null} */
  #observedSummary = null;

  #summaryResizeObserver = new ResizeObserver(() => {
    this.#updateStickyState();
  });

  #promoCloseTimer = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('keydown', this.#handlePromoEscape, true);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);
    this.addEventListener('click', this.#handlePromoDismiss, true);
    this.addEventListener('toggle', this.#handlePromoToggle, true);
    document.addEventListener('cart-discount:applied', this.#handleDiscountApplied);
    window.visualViewport?.addEventListener('resize', this.#updatePromoKeyboardOffset);
    window.visualViewport?.addEventListener('scroll', this.#updatePromoKeyboardOffset);
    this.#updateStickyState();
    this.#updatePromoKeyboardOffset();
    this.#syncPromoState();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('keydown', this.#handlePromoEscape, true);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
    this.removeEventListener('click', this.#handlePromoDismiss, true);
    this.removeEventListener('toggle', this.#handlePromoToggle, true);
    document.removeEventListener('cart-discount:applied', this.#handleDiscountApplied);
    window.visualViewport?.removeEventListener('resize', this.#updatePromoKeyboardOffset);
    window.visualViewport?.removeEventListener('scroll', this.#updatePromoKeyboardOffset);
    this.#summaryResizeObserver.disconnect();
    this.#observedSummary = null;
    window.clearTimeout(this.#promoCloseTimer);
  }

  updatedCallback() {
    super.updatedCallback();
    requestAnimationFrame(() => {
      this.#updateStickyState();
      this.#syncPromoState();
    });
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
  };

  #handleDrawerClose = () => {
    window.clearTimeout(this.#promoCloseTimer);
    const details = this.querySelector('.cart-discount details');
    if (details instanceof HTMLDetailsElement) {
      details.removeAttribute('data-closing');
      details.open = false;
    }
    this.#syncPromoState();
  };

  #handlePromoToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement) || !event.target.closest('.cart-discount')) return;
    this.#syncPromoState();
    const details = event.target;
    if (details.open) details.removeAttribute('data-closing');
    if (details.open && document.activeElement === details.querySelector('summary')) {
      requestAnimationFrame(() => details.querySelector('input[name="discount"]')?.focus({ preventScroll: true }));
    }
  };

  #handlePromoEscape = (event) => {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented || !this.#themeDrawer?.isOpen) return;
    const details = this.querySelector('.cart-discount details');
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (document.querySelectorAll('dialog:modal').length > 1) return;
    event.preventDefault();
    event.stopPropagation();
    this.#closePromoSheet();
  };

  #handlePromoDismiss = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.cart-discount [data-accordion-close]')) return;
    if (!this.querySelector('.cart-discount details[open]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#closePromoSheet();
  };

  #handleDiscountApplied = (event) => {
    if (event.detail?.sectionId !== 'cart-drawer-section' || !this.#themeDrawer?.isOpen) return;
    this.#closePromoSheet();
  };

  #closePromoSheet() {
    const details = this.querySelector('.cart-discount details[open]');
    if (!(details instanceof HTMLDetailsElement) || details.hasAttribute('data-closing')) return;
    const finish = () => {
      if (!details.isConnected) return;
      details.open = false;
      details.removeAttribute('data-closing');
      details.querySelector('summary')?.focus({ preventScroll: true });
      this.#syncPromoState();
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }
    details.setAttribute('data-closing', '');
    this.#promoCloseTimer = window.setTimeout(finish, 200);
  }

  #syncPromoState() {
    const details = this.querySelector('.cart-discount details');
    const items = this.querySelector('.cart-drawer__items');
    if (items instanceof HTMLElement) items.inert = Boolean(details instanceof HTMLDetailsElement && details.open);
  }

  #updatePromoKeyboardOffset = () => {
    const viewport = window.visualViewport;
    const offset = window.matchMedia('(max-width: 989px)').matches && viewport
      ? Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop))
      : 0;
    this.#dialog?.style.setProperty('--cart-promo-keyboard-offset', `${offset}px`);
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      this.#summaryResizeObserver.disconnect();
      this.#observedSummary = null;
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    if (this.#observedSummary !== summary) {
      this.#summaryResizeObserver.disconnect();
      this.#summaryResizeObserver.observe(summary);
      this.#observedSummary = summary;
    }
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
